/**
 * InspectorDrawer — right slide-over panel showing details for a selected asset.
 *
 * Slides in from the right, Escape closes it.
 */

import { useEffect } from 'react';
import './InspectorDrawer.css';

export type InspectorStatus = 'active' | 'idle' | 'fault' | 'warning';

export interface InspectorData {
  id: string;
  label: string;
  status: InspectorStatus;
  electrical?: {
    power?: number;    // kW
    current?: number;  // A
    voltage?: number;  // V
    efficiency?: number; // %
  };
  physical?: {
    tilt?: number;     // degrees
    azimuth?: number;  // degrees
    rows?: number;
    modules?: number;
  };
  meta?: {
    lastSeen?: string;
    serialNo?: string;
    installDate?: string;
  };
}

interface InspectorDrawerProps {
  data: InspectorData | null;
  onClose: () => void;
}

function StatusBadge({ status }: { status: InspectorStatus }) {
  const labels: Record<InspectorStatus, string> = {
    active:  'Active',
    idle:    'Idle',
    fault:   'Fault',
    warning: 'Warning',
  };
  return (
    <span className={`insp-badge insp-badge--${status}`}>
      <span className="insp-badge__dot" />
      {labels[status]}
    </span>
  );
}

function FieldRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="insp-field">
      <span className="insp-field__label">{label}</span>
      <span className="insp-field__value tabular">{value}</span>
    </div>
  );
}

export default function InspectorDrawer({ data, onClose }: InspectorDrawerProps) {
  // Escape key handler
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const isOpen = data !== null;

  return (
    <>
      {/* Backdrop (click to close on mobile) */}
      {isOpen && <div className="insp-backdrop" onClick={onClose} aria-hidden />}

      {/* Drawer */}
      <div className={`insp-drawer${isOpen ? ' insp-drawer--open' : ''}`} role="complementary" aria-label="Inspector">
        {data ? (
          <>
            {/* Header */}
            <div className="insp-header">
              <div className="insp-header__left">
                <h2 className="insp-header__title">{data.label}</h2>
                <StatusBadge status={data.status} />
              </div>
              <button type="button" className="insp-close" onClick={onClose} aria-label="Close inspector">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M18 6L6 18M6 6l12 12"/>
                </svg>
              </button>
            </div>

            {/* Content */}
            <div className="insp-body">
              {/* Electrical section */}
              {data.electrical && (
                <section className="insp-section">
                  <h3 className="insp-section__title">Electrical</h3>
                  {data.electrical.power !== undefined && (
                    <FieldRow label="Power" value={`${data.electrical.power.toFixed(2)} kW`} />
                  )}
                  {data.electrical.current !== undefined && (
                    <FieldRow label="Current" value={`${data.electrical.current.toFixed(1)} A`} />
                  )}
                  {data.electrical.voltage !== undefined && (
                    <FieldRow label="Voltage" value={`${data.electrical.voltage.toFixed(1)} V`} />
                  )}
                  {data.electrical.efficiency !== undefined && (
                    <FieldRow label="Efficiency" value={`${data.electrical.efficiency.toFixed(0)}%`} />
                  )}
                </section>
              )}

              {/* Physical section */}
              {data.physical && (
                <section className="insp-section">
                  <h3 className="insp-section__title">Physical</h3>
                  {data.physical.tilt !== undefined && (
                    <FieldRow label="Tilt" value={`${data.physical.tilt}°`} />
                  )}
                  {data.physical.azimuth !== undefined && (
                    <FieldRow label="Azimuth" value={`${data.physical.azimuth}°`} />
                  )}
                  {data.physical.rows !== undefined && (
                    <FieldRow label="Rows" value={data.physical.rows} />
                  )}
                  {data.physical.modules !== undefined && (
                    <FieldRow label="Modules" value={data.physical.modules} />
                  )}
                </section>
              )}

              {/* Meta section */}
              {data.meta && (
                <section className="insp-section">
                  <h3 className="insp-section__title">Info</h3>
                  {data.meta.lastSeen && (
                    <FieldRow label="Last seen" value={data.meta.lastSeen} />
                  )}
                  {data.meta.serialNo && (
                    <FieldRow label="Serial No." value={data.meta.serialNo} />
                  )}
                  {data.meta.installDate && (
                    <FieldRow label="Installed" value={data.meta.installDate} />
                  )}
                </section>
              )}
            </div>
          </>
        ) : null}
      </div>
    </>
  );
}

// ─── Mock data helper ─────────────────────────────────────────────────────────

export function getMockInspectorData(id: string): InspectorData {
  const isFault = id.includes('1-3') || id.includes('4-5');
  const isIdle  = id.includes('2-3') || id.includes('4-3') || id.includes('3-idle');

  return {
    id,
    label: id.replace('str-', 'STR ').replace(/-/g, '.'),
    status: isFault ? 'fault' : isIdle ? 'idle' : 'active',
    electrical: {
      power:      isFault ? 0 : isIdle ? 0 : +(Math.random() * 0.4 + 0.7).toFixed(2),
      current:    isFault ? 0 : isIdle ? 0 : +(Math.random() * 1 + 3.8).toFixed(1),
      voltage:    isFault ? 0 : isIdle ? 0 : +(Math.random() * 4 + 50).toFixed(1),
      efficiency: isFault ? 0 : isIdle ? 0 : +(Math.random() * 5 + 93).toFixed(0),
    },
    physical: {
      tilt:    30,
      azimuth: 180,
      rows:    2,
      modules: 14,
    },
    meta: {
      lastSeen:    isFault ? '09:45' : isIdle ? '08:10' : 'Just now',
      serialNo:    `SLD-${Math.random().toString(36).substr(2, 8).toUpperCase()}`,
      installDate: '2023-04-15',
    },
  };
}
